import { Button, Grid, makeStyles } from "@material-ui/core";
import { tokenVerifyMutation } from "@saleor/auth/mutations";
import {
  VerifyToken,
  VerifyTokenVariables
} from "@saleor/auth/types/VerifyToken";
import { getTokenCookies, getTokens } from "@saleor/auth/utils";
import AppHeader from "@saleor/components/AppHeader";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import { IS_TOKEN_FROM_COOKIES } from "@saleor/config";
import { ASSIGNEE_CHOICES } from "@saleor/coupons/constants";
import { couponFullDetails_coupon } from "@saleor/coupons/types/CouponFullDetails";
import { MallTags_malls_edges_node } from "@saleor/coupons/types/MallTags";
import {
  canManageCouponUsageLimitByDealType,
  checkCouponPermissionAccess,
  hasAnyCouponUsageLimitPermissionChanged,
  resolveCouponUsageLimitForCreate
} from "@saleor/coupons/utils";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { User_userPermissions } from "@saleor/fragments/types/User";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCouponCollection_search_edges_node } from "@saleor/searches/types/SearchCouponCollection";
import { FetchMoreProps, UserError } from "@saleor/types";
import {
  CouponAssignee,
  CouponAssigneeEnum,
  CouponDealTypeEnum,
  CouponOccurenceEnum,
  CouponRedeemTypeEnum,
  StaffPromoCreatorEnum
} from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import {
  createCouponValidation,
  draftCouponCreateValidation,
  hasNoError
} from "@saleor/utils/validators/coupon";
import { RawDraftContentState } from "draft-js";
import React, { useMemo } from "react";
import { useMutation } from "react-apollo";
import { FormattedMessage, useIntl } from "react-intl";

import { CouponAccessImage } from "../CouponAccessImage";
import CouponDetails from "../CouponDetails/CouponDetails";
import { CouponInformation } from "../CouponInformation";
import CouponPreview from "../CouponPreview/CouponPreview";
import CouponPromoImage from "../CouponPromoImage";
import CouponScheduling from "../CouponScheduling/CouponScheduling";
import CouponTaggingCard from "../CouponTaggingCard/CouponTaggingCard";
import { CouponUsage } from "../CouponUsage";

const useStyles = makeStyles(
  theme => ({
    container: {
      alignItems: "stretch",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",

      [theme.breakpoints.down("sm")]: {
        flexDirection: "column"
      }
    },
    gridItem: {
      flex: 1
    },
    pageGrid: {
      display: "grid",
      gridColumnGap: "24px",
      gridRowGap: "24px",
      gridTemplateColumns: "11fr 4fr"
    },
    sideGrid: {
      width: "100%"
    }
  }),
  { name: "CouponCreatePage" }
);

export interface ErrorValidation {
  accessPageDescription: string;
  couponCode: string;
  couponTitle: string;
  couponValue: number;
  mainPromoDescription: string;
  mainPromoTitle: string;
  mechanics: string;
  redeemType: string;
  subtitle: string;
  tagline: string;
  title: string;
  totalCopies: number;
}

export interface CouponCreateFormData {
  accessImage: string;
  accessImageAlt: string;
  assignee: CouponAssignee;
  couponCode: string;
  couponTitle: string;
  mainPromoDescription: RawDraftContentState;
  couponValue: number;
  isFeatured: boolean;
  isAppExclusive: boolean;
  isHidden: boolean;
  isNationwide: boolean;
  isUsageCustomTimer: boolean;
  isUsageTimerApplied: boolean;
  malls: string[];
  hasMalls: boolean;
  mainPromoTitle: string;
  promoImage: string;
  promoImageAlt: string;
  mechanics: RawDraftContentState;
  subtitle: string;
  tagline: string;
  accessPageDescription: RawDraftContentState;
  couponStartDate: string;
  couponStartTime: string;
  couponEndDate: string;
  couponEndTime: string;
  couponStatus: string;
  excludedDays: string[];
  occurrence: string;
  redeemType: string;
  redeemingStartDate: string;
  redeemingStartTime: string;
  redeemingEndDate: string;
  redeemingEndTime: string;
  totalCopies: number | "";
  userRestriction: string;
  usageLimit: number | "";
  usageLimitToggle: string;
  usageTimerHour: number;
  usageTimerMinute: number;
  usageTimerSecond: number;
  couponClassification: string;
  eventLocation: string;
  eventStartDate: string;
  eventStartTime: string;
  eventEndDate: string;
  eventEndTime: string;
  category: string;
  couponCollection: string;
  isExclusiveRedemption: boolean;
}
export interface CouponCreatePageProps {
  id: string;
  disabled: boolean;
  userPermissions: User_userPermissions[];
  coupon?: couponFullDetails_coupon;
  errors: DiscountErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  malls: MallTags_malls_edges_node[];
  promoImage: any;
  accessImage: any;
  selectedCategory: string[];
  fetchMoreMalls: FetchMoreProps;
  fetchMall: (query: string) => void;
  onAccessImageUpload: (file: File) => void;
  onPromoImageUpload: (file: File) => void;
  fetchMoreCollection?: FetchMoreProps;
  fetchCollections?: (query: string) => void;
  setSelectedCategory: React.Dispatch<React.SetStateAction<any[]>>;
  couponCollections: SearchCouponCollection_search_edges_node[];
  onBack: () => void;
  onSubmit: (data: CouponCreateFormData) => void;
  onDraft: (data: CouponCreateFormData) => void;
}

export interface CouponError extends UserError {
  message: string;
}

export const couponErrors: CouponError[] = [
  { field: undefined, message: "Error Coupon Message 1" },
  { field: undefined, message: "Error Coupon Message 2" },
  { field: undefined, message: "Error Coupon Message 3" }
];

export const CouponCreatePage: React.FC<CouponCreatePageProps> = props => {
  const {
    malls,
    disabled,
    userPermissions,
    onAccessImageUpload,
    onPromoImageUpload,
    onSubmit,
    onDraft,
    fetchMall,
    fetchMoreMalls,
    errors,
    saveButtonBarState,
    promoImage,
    accessImage,
    selectedCategory,
    onBack,
    couponCollections,
    fetchMoreCollection,
    fetchCollections,
    setSelectedCategory
  } = props;
  const [validator, setValidator] = React.useState({
    accessPageDescription: null,
    assignee: null,
    category: null,
    couponCode: null,
    couponEndDate: null,
    couponEndTime: null,
    couponStartDate: null,
    couponStartTime: null,
    couponValue: null,
    eventLocation: null,
    excludedDays: null,
    mainPromoDescription: null,
    mainPromoTitle: null,
    mechanics: null,
    occurrence: null,
    redeemType: null,
    redeemingEndDate: null,
    redeemingEndTime: null,
    redeemingStartDate: null,
    redeemingStartTime: null,
    subtitle: null,
    tagline: null,
    totalCopies: null,
    usageLimit: null,
    usageTimerHour: null,
    usageTimerMinute: null,
    usageTimerSecond: null
  });

  const { user } = useUser();

  const userPromoCreator = user?.promoCreator;

  const isUserTypeNull = useMemo(
    () => user?.permissionGroups.some(group => group.smopUsertypeCode === null),
    [user]
  );

  const initialMallIds = useMemo(() => {
    if (isUserTypeNull && user?.malls?.edges.length > 0) {
      return user.malls.edges.map(edge => edge.node.id);
    }
    return [];
  }, [user, isUserTypeNull]);

  const initialCouponAssignee = useMemo(() => {
    if (userPromoCreator === StaffPromoCreatorEnum.APP_DEVELOPMENT) {
      return CouponAssignee.APP_DEVELOPMENT;
    } else if (userPromoCreator === StaffPromoCreatorEnum.MALL_MARKETING) {
      return CouponAssignee.MALL_MARKETING;
    } else {
      return CouponAssignee.SM_DEALS;
    }
  }, [user, userPromoCreator]);

  let assigneeChoices = ASSIGNEE_CHOICES;

  if (userPromoCreator === StaffPromoCreatorEnum.SMO_MARKETING) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value !== CouponAssigneeEnum.APP_DEVELOPMENT
    );
  } else if (userPromoCreator === StaffPromoCreatorEnum.MALL_MARKETING) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value === CouponAssigneeEnum.MALL_MARKETING
    );
  } else if (userPromoCreator === StaffPromoCreatorEnum.APP_DEVELOPMENT) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value === CouponAssigneeEnum.APP_DEVELOPMENT
    );
  }

  const initialForm: CouponCreateFormData = useMemo(
    () => ({
      accessImage: "",
      accessImageAlt: "",
      accessPageDescription: null,
      assignee: initialCouponAssignee,
      category: null,
      couponClassification: CouponDealTypeEnum.DEALS,
      couponCode: "",
      couponCollection: "",
      couponEndDate: "",
      couponEndTime: "23:59",
      couponStartDate: "",
      couponStartTime: "00:00",
      couponStatus: "",
      couponTitle: "",
      couponValue: null,
      eventEndDate: "",
      eventEndTime: "23:59",
      eventLocation: "",
      eventStartDate: "",
      eventStartTime: "00:00",
      excludedDays: [],
      hasMalls: true,
      isAppExclusive: false,
      isExclusiveRedemption: false,
      isFeatured: false,
      isHidden: false,
      isNationwide: false,
      isUsageCustomTimer: false,
      isUsageTimerApplied: false,
      mainPromoDescription: null,
      mainPromoTitle: "",
      malls: initialMallIds,
      mechanics: null,
      occurrence: CouponOccurenceEnum.DAILY,
      promoImage: "",
      promoImageAlt: "",
      redeemType: CouponRedeemTypeEnum.REDEEM_LATER,
      redeemingEndDate: "",
      redeemingEndTime: "23:59",
      redeemingStartDate: "",
      redeemingStartTime: "00:00",
      subtitle: "",
      tagline: "",
      totalCopies: null,
      usageLimit: 1,
      usageLimitToggle: "once",
      usageTimerHour: 23,
      usageTimerMinute: 59,
      usageTimerSecond: 59,
      userRestriction: "ALL"
    }),
    [initialMallIds]
  );
  const notify = useNotifier();
  const classes = useStyles(props);
  const intl = useIntl();
  const [previewCoupon, setPreviewCoupon] = React.useState(false);
  const [currentUserPermissions, setCurrentUserPermissions] = React.useState<
    User_userPermissions[]
  >(
    (userPermissions?.filter(Boolean) as User_userPermissions[]) ||
      user?.userPermissions?.filter(Boolean) ||
      []
  );

  React.useEffect(() => {
    if (userPermissions?.length) {
      setCurrentUserPermissions(
        userPermissions.filter(Boolean) as User_userPermissions[]
      );
    } else if (user?.userPermissions?.length) {
      setCurrentUserPermissions(
        user.userPermissions.filter(Boolean) as User_userPermissions[]
      );
    }
  }, [userPermissions, user?.userPermissions]);

  const [verifyTokenMutation] = useMutation<VerifyToken, VerifyTokenVariables>(
    tokenVerifyMutation
  );

  const isVerifyingRef = React.useRef(false);
  const currentUserPermissionsRef = React.useRef(currentUserPermissions);
  currentUserPermissionsRef.current = currentUserPermissions;

  const lastPermissionUpdateRef = React.useRef(
    typeof window !== "undefined"
      ? localStorage.getItem("coupon_usage_limit_permission_updated") || ""
      : ""
  );

  const refreshPermissions = React.useCallback(
    async (force: boolean = false) => {
      if (isVerifyingRef.current) {
        return;
      }

      const token = IS_TOKEN_FROM_COOKIES
        ? getTokenCookies().auth
        : getTokens().auth;

      if (token) {
        try {
          isVerifyingRef.current = true;
          const verifyResult = await verifyTokenMutation({
            variables: { token }
          });
          const latestPerms =
            verifyResult?.data?.tokenVerify?.user?.userPermissions;
          if (latestPerms && Array.isArray(latestPerms)) {
            const validPerms = latestPerms.filter(
              Boolean
            ) as User_userPermissions[];

            if (
              force ||
              hasAnyCouponUsageLimitPermissionChanged(
                currentUserPermissionsRef.current,
                validPerms
              )
            ) {
              setCurrentUserPermissions(validPerms);
            }
          }
        } catch (err) {
          // Silently ignore error
        } finally {
          isVerifyingRef.current = false;
        }
      }
    },
    [verifyTokenMutation]
  );

  React.useEffect(() => {
    refreshPermissions(true);

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "coupon_usage_limit_permission_updated") {
        lastPermissionUpdateRef.current = event.newValue || "";
        refreshPermissions(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentUpdate =
          localStorage.getItem("coupon_usage_limit_permission_updated") || "";
        if (
          currentUpdate &&
          currentUpdate !== lastPermissionUpdateRef.current
        ) {
          lastPermissionUpdateRef.current = currentUpdate;
          refreshPermissions(true);
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPermissions]);

  const userHasFeatureCouponAccess = Boolean(
    checkCouponPermissionAccess(currentUserPermissions, "FEATURE_COUPONS")
  );

  const normalizeCreateFormData = (
    data: CouponCreateFormData
  ): CouponCreateFormData => ({
    ...data,
    usageLimit: resolveCouponUsageLimitForCreate(
      canManageCouponUsageLimitByDealType(
        currentUserPermissions,
        data.couponClassification
      ),
      data.usageLimit
    ),
    usageLimitToggle: data.usageLimitToggle || "once"
  });

  const handleValidation = data => {
    const canManageUsageLimit = canManageCouponUsageLimitByDealType(
      currentUserPermissions,
      data.couponClassification
    );
    const validate = createCouponValidation(data, {
      canManageCouponUsageLimit: canManageUsageLimit
    });
    setValidator(validate);
    if (validate.imageUpload) {
      notify({
        status: "error",
        text: validate.imageUpload,
        title: "Coupon Form Error"
      });
    }
    if (validate?.formDataValidations) {
      notify({
        status: "error",
        text: `${validate.formDataValidations?.text}`,
        title: `${validate.formDataValidations.title}`
      });
    }
    return validate;
  };

  const handleDraftCreateValidation = data => {
    const validate = draftCouponCreateValidation(data);
    setValidator(prevState => ({
      ...prevState,
      ...validate
    }));

    if (validate.mainPromoTitle) {
      notify({
        status: "error",
        text: "Main Promo Title is required when saving a draft",
        title: "Coupon Form Error"
      });
    }

    return validate;
  };

  const handlePreviewModal = data => {
    const normalizedData = normalizeCreateFormData(data);
    const validationResult = handleValidation(normalizedData);
    if (hasNoError(validationResult)) {
      setPreviewCoupon(!previewCoupon);
    }
  };

  const handleSubmit = (data: CouponCreateFormData) => {
    const normalizedData = normalizeCreateFormData(data);
    const validationResult = handleValidation(normalizedData);
    if (hasNoError(validationResult)) {
      onSubmit(normalizedData);
    }
  };

  const handleSubmitDraft = (data: CouponCreateFormData) => {
    const normalizedData = normalizeCreateFormData(data);
    const validationResult = handleDraftCreateValidation(normalizedData);
    if (hasNoError(validationResult)) {
      onDraft(normalizedData);
    }
  };

  const [hostMallDisplay, setHostMallDisplay] = useStateFromProps("");
  const [
    couponCollectionDisplayName,
    setCouponCollectionDisplayName
  ] = useStateFromProps("");

  const hostMallChoices = maybe(
    () =>
      malls.map(mall => ({
        label: mall?.name,
        value: mall?.id
      })),
    []
  );

  const collectionChoiceList: SingleAutocompleteChoiceType[] = couponCollections.map(
    item => ({
      label: item.title,
      value: item.id
    })
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ change, data, hasChanged, submit }) => {
        const handleSelectHostMall = createSingleAutocompleteSelectHandler(
          change,
          setHostMallDisplay,
          hostMallChoices
        );

        const handleSelectCollection = createSingleAutocompleteSelectHandler(
          change,
          setCouponCollectionDisplayName,
          collectionChoiceList
        );

        const handleChangeWithCouponCollectionClear = event => {
          change(event);
          change({ target: { name: "couponCollection", value: "" } });
          change({ target: { name: "isExclusiveRedemption", value: false } });
          setCouponCollectionDisplayName("");
          if (event.target.name === "couponClassification") {
            change({ target: { name: "malls", value: [] } });
            setHostMallDisplay("");
          }
          if (event.target.value === CouponDealTypeEnum.EVENTS) {
            change({
              target: {
                name: "isAppExclusive",
                value: false
              }
            });
          }
        };

        return (
          <Container>
            <AppHeader onBack={onBack}>
              {intl.formatMessage(sectionNames.coupons)}
            </AppHeader>
            <PageHeader title={"Coupons"}>
              <Button
                id="previewModalBtn"
                onClick={() => handlePreviewModal(data)}
                variant="outlined"
                color="primary"
              >
                <FormattedMessage
                  defaultMessage="PREVIEW COUPON"
                  description="button"
                />
              </Button>
            </PageHeader>
            <Grid container className={classes.pageGrid}>
              <div>
                <CouponDetails
                  assigneeChoices={assigneeChoices}
                  selectedCategory={selectedCategory}
                  data={data}
                  disabled={false}
                  onChange={change}
                  fetchMalls={fetchMall}
                  fetchMoreMalls={fetchMoreMalls}
                  malls={malls}
                  error={validator}
                  isFeatured={false}
                  onMallChange={handleSelectHostMall}
                  hostMall={hostMallChoices}
                  hostMallDisplay={hostMallDisplay}
                  setSelectedCategory={setSelectedCategory}
                  couponCollections={collectionChoiceList}
                  fetchMoreCollection={fetchMoreCollection}
                  fetchCollections={fetchCollections}
                  onCouponCollectionChange={handleSelectCollection}
                  couponCollectionDisplayValue={couponCollectionDisplayName}
                  onCouponCategoryChange={handleChangeWithCouponCollectionClear}
                  onCouponClassificationChange={
                    handleChangeWithCouponCollectionClear
                  }
                />
                <Grid container spacing={2} className={classes.container}>
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <CouponPromoImage
                      data={data}
                      errors={errors}
                      disabled={false}
                      onChange={change}
                      onPromoImageUpload={onPromoImageUpload}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <CouponAccessImage
                      data={data}
                      errors={errors}
                      disabled={false}
                      onChange={change}
                      onAccessImageUpload={onAccessImageUpload}
                    />
                  </Grid>
                </Grid>
                <FormSpacer />
                <CouponInformation
                  data={data}
                  disabled={false}
                  errors={couponErrors}
                  localError={validator}
                  onChange={change}
                />
                <CouponScheduling
                  data={data}
                  onChange={change}
                  localError={validator}
                  disabled={disabled}
                />
              </div>
              <div className={classes.sideGrid}>
                {userHasFeatureCouponAccess && (
                  <CouponTaggingCard
                    isFeatured={data.isFeatured}
                    isAppExclusive={data.isAppExclusive}
                    isAppExclusiveHidden={
                      data.couponClassification === CouponDealTypeEnum.EVENTS
                    }
                    onChange={change}
                  />
                )}
                <CouponUsage
                  data={data}
                  disabled={false}
                  onChange={change}
                  localError={validator}
                  userPermissions={currentUserPermissions}
                />
              </div>
              <CouponPreview
                data={data}
                open={previewCoupon}
                mainPromoImg={promoImage}
                accessPageImg={accessImage}
                onClose={() => setPreviewCoupon(false)}
                malls={malls}
              />
            </Grid>
            <div>
              <SaveButtonBar
                state={saveButtonBarState}
                onCancel={onBack}
                onSave={submit}
                disabled={!hasChanged}
                hideSave={true}
                onDelete={undefined}
                onPublish={submit}
                onDraft={() => handleSubmitDraft(data)}
              />
            </div>
          </Container>
        );
      }}
    </Form>
  );
};

export default CouponCreatePage;
